import { useState, useEffect } from 'react';
import { useAuth } from "../../../contexts/AuthContext";
import { useGetBiddingDateForApplyQuery } from '../../../hooks/useCalendarQuery';
import { useUpdateBiddingNumberMutation } from '../../../hooks/useBiddingItemsQuery';
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { RefreshCw } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { getFilterPreference, saveFilterPreference } from '../../../utils/filterStorage';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault("Asia/Tokyo");

const PrefectureTypeOptions = [
  { value: 1, label: '通常入札 - 県内扱' },
  { value: 2, label: '通常入札 - 県外扱' },
];

export const ManualNumberingPage = () => {
  const queryClient = useQueryClient();
  const { currentUser } = useAuth();
  
  // 1. カレンダー初期値の取得
  const { data: initialBiddingDate, isLoading: isDateLoading } = useGetBiddingDateForApplyQuery(currentUser?.loginId);
  
  // 2. 採番Mutation
  const { mutate: updateNumbering, isPending: isNumbering } = useUpdateBiddingNumberMutation();

  const [localParams, setLocalParams] = useState({
    targetDate: '',
    prefectureType: 1,
    listingCategory: 2,
  });

  // 初期化完了フラグ
  const [isInitialized, setIsInitialized] = useState(false);

  // 3. 初期化ロジック
  useEffect(() => {
    // 読み込み中、または認証情報がない場合はスキップ
    if (isDateLoading || !currentUser || isInitialized) return;

    // APIから取得した日付があればそれを使用、なければ今日の日付を使用
    const rawDate = initialBiddingDate 
      ? String(initialBiddingDate) 
      : dayjs().tz().format("YYYYMMDD");

    // YYYYMMDD -> YYYY-MM-DD
    const formatted = dayjs(rawDate.replace(/^(\d{4})(\d{2})(\d{2})$/, "$1-$2-$3")).format("YYYY-MM-DD");
    
    const preference = getFilterPreference();
    const defaultlistingCategory = preference.category; 
    const defaultPref = preference.prefecture;

    setLocalParams(prev => ({ ...prev, 
      targetDate: formatted, 
      prefectureType: defaultPref,
      listingCategory: defaultlistingCategory,
    }));
    setIsInitialized(true); 
  }, [initialBiddingDate, isDateLoading, currentUser, isInitialized]);

  const handleChange = (field: string, value: string | number) => {
    setLocalParams(prev => ({ ...prev, [field]: value }));
    saveFilterPreference(2, Number(value));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!localParams.targetDate) return;

    const biddingDateNum = Number(dayjs(localParams.targetDate).format("YYYYMMDD"));
    const prefectureTypeNum = Number(localParams.prefectureType);
    const listingCategoryNum = Number(localParams.listingCategory);

    updateNumbering({
        biddingDate: biddingDateNum,
        listingCategory: listingCategoryNum,
        prefectureType: prefectureTypeNum,
      });
  };

  // 一括更新用のハンドラ
  const handleRefreshAll = async () => {
    // ヘッダーの件数（CommonLayoutで使用しているキー）のキャッシュを無効化
    queryClient.invalidateQueries({ queryKey: ['biddingitemsstatusupdate'] });
  };

  // 全体の読み込み状態
  const isPageLoading = isDateLoading || !isInitialized;

  if (isPageLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>読み込み中...</p>
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen font-sans">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-12">
        <header className="mb-8">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-3xl font-extrabold text-slate-800">
              入札Noの手動採番
            </h1>
          </div>

          <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
            <div className="flex flex-wrap items-end gap-6 w-full">
              {/* 入札日 */}
              <div className="w-full sm:w-auto">
                <label className="block text-sm font-bold text-slate-700 mb-1">
                  入札日
                </label>
                <input
                  type="date"
                  value={localParams.targetDate}
                  onChange={(e) => handleChange('targetDate', e.target.value)}
                  className="w-full sm:w-48 p-2 h-[42px] border border-slate-300 rounded-md bg-white focus:ring-indigo-500 appearance-none min-w-0 safari-date-fix"
                />
              </div>

              {/* 出品区分 */}
              <div className="w-full sm:w-auto">
                <label className="block text-sm font-bold text-slate-700 mb-1">
                  出品区分（県内/県外）
                </label>
                <select
                  value={localParams.prefectureType}
                  onChange={(e) => handleChange('prefectureType', e.target.value)}
                  className="w-full sm:w-56 p-2 h-[42px] border border-slate-300 rounded-md bg-white focus:ring-indigo-500"
                >
                  {PrefectureTypeOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              {/* ボタン */}
              <div className="ml-auto">
                <button
                  disabled={isNumbering || !localParams.targetDate}
                  onClick={handleSubmit}
                  className={`min-w-[140px] px-6 py-2 text-white rounded shadow-sm font-bold transition-all
                    ${(isNumbering || !localParams.targetDate)
                      ? 'bg-slate-300 cursor-not-allowed'
                      : 'bg-indigo-600 hover:bg-indigo-700 active:scale-95'
                    }`}
                >
                  {isNumbering ? '採番中...' : '手動採番'}
                </button>
              </div>
            </div>
          </div>
        </header>
      </div>
      <button
        onClick={handleRefreshAll}
        disabled={isPageLoading}
        className="fixed bottom-6 left-6 sm:bottom-10 sm:left-10 z-30 p-3 sm:p-3.5 bg-indigo-600 text-white rounded-full shadow-lg hover:bg-indigo-700 hover:shadow-xl transition-all active:scale-95 disabled:bg-indigo-400 focus:outline-none focus:ring-4 focus:ring-indigo-300"
        title="最新の情報に更新"
      >
        <RefreshCw className={`w-5 h-5 sm:w-6 sm:h-6`} />
      </button>
    </div>
  );
};